import { Box } from "@mui/material";
import { alpha, keyframes, styled, useTheme } from "@mui/material/styles";
import PageContainer from "./PageContainer";

type SpinnerFullPageProps = {
  /** Pixel size of the SVG (width & height). Default: 128 */
  size?: number;
  /** Base hue (0–360) for the faint ring stroke. Default: 223 */
  hue?: number;
};

const bump9 = keyframes`
  from,42%,46%,51%,55%,59%,63%,67%,71%,74%,78%,81%,85%,88%,92%,to { transform: translate(0,0); }
  44% { transform: translate(1.33%,6.75%); }
  53% { transform: translate(-16.67%,-0.54%); }
  61% { transform: translate(3.66%,-2.46%); }
  69% { transform: translate(-0.59%,15.27%); }
  76% { transform: translate(-1.92%,-4.68%); }
  83% { transform: translate(9.38%,0.96%); }
  90% { transform: translate(-4.55%,1.98%); }
`;

const worm9 = keyframes`
  from { stroke-dashoffset: 10; }
  25% { stroke-dashoffset: 295; }
  to { stroke-dashoffset: 1165; }
`;

const SvgLoader = styled("svg")({
  width: "4em",
  height: "4em",
  display: "block",
  animation: `${bump9} 3s linear infinite`,
  ".pl__ring": {
    transition: "stroke 0.3s",
  },
  ".pl__worm": {
    animation: `${worm9} 3s cubic-bezier(0.42,0.17,0.75,0.83) infinite`,
  },
});

const SpinnerFullPage: React.FC<SpinnerFullPageProps> = ({
  size = 128,
  hue = 223,
}) => {
  const theme = useTheme();
  // expose hue as a CSS var so the ring stroke matches the original snippet
  const ringStroke =
    theme.palette.mode === "dark"
      ? alpha(theme.palette.text.secondaryOnBrand, 0.22) // light ring on dark bg
      : alpha(theme.palette.text.primaryOnBrand, 0.12); // dark ring on light bg

  return (
    <PageContainer>
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <SvgLoader
          className="pl"
          viewBox="0 0 128 128"
          width={size}
          height={size}
          role="img"
          aria-label="Loading"
        >
          <defs>
            <linearGradient id="pl-grad" x1="0" y1="0" x2="0" y2="1">
              {/* feel free to theme these stops */}
              <stop
                offset="0%"
                stopColor={theme.palette.background.brandSolidSecondaryLighter}
              />
              <stop
                offset="100%"
                stopColor={theme.palette.background.brandSolidSecondary}
              />
            </linearGradient>
          </defs>

          <circle
            className="pl__ring"
            r="56"
            cx="64"
            cy="64"
            fill="none"
            stroke={ringStroke}
            strokeWidth="16"
            strokeLinecap="round"
          />

          <path
            className="pl__worm"
            d="M92,15.492S78.194,4.967,66.743,16.887c-17.231,17.938-28.26,96.974-28.26,96.974L119.85,59.892l-99-31.588,57.528,89.832L97.8,19.349,13.636,88.51l89.012,16.015S81.908,38.332,66.1,22.337C50.114,6.156,36,15.492,36,15.492a56,56,0,1,0,56,0Z"
            fill="none"
            stroke="url(#pl-grad)"
            strokeWidth="16"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="44 1111"
            strokeDashoffset="10"
          />
        </SvgLoader>
      </Box>
    </PageContainer>
  );
};

export default SpinnerFullPage;
